package com.eladfinish.bible.contest.screens.lexicographicalSort.usecases

import com.eladfinish.bible.contest.network.ApiBaseUrlProvider
import com.eladfinish.bible.contest.network.isNetworkConnectivityError
import com.eladfinish.contract.email.SendPdfEmailRequest
import com.eladfinish.contract.email.SendPdfEmailResponse
import io.github.aakira.napier.Napier
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.timeout
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Sends an email (with a PDF attachment) to the given address, via the app's backend
 * (which in turn uses Brevo's Transactional Email API).
 */
interface SendPdfEmailUseCase {
    suspend operator fun invoke(
        firstName: String,
        lastName: String,
        email: String,
        pdfBytes: ByteArray,
        subject: String,
        htmlContent: String,
        fileName: String = "demo.pdf",
    ): Result<Unit>
}

class SendPdfEmailUseCaseImpl(
    private val httpClient: HttpClient,
    private val apiBaseUrlProvider: ApiBaseUrlProvider,
) : SendPdfEmailUseCase {

    @OptIn(ExperimentalEncodingApi::class)
    override suspend fun invoke(
        firstName: String,
        lastName: String,
        email: String,
        pdfBytes: ByteArray,
        subject: String,
        htmlContent: String,
        fileName: String,
    ): Result<Unit> {
        val pdfBase64 = Base64.encode(pdfBytes)

        Napier.d(
            message = "send_pdf_email payload: pdfBytes=${pdfBytes.size}B, base64Len=${pdfBase64.length}",
            tag = "SendPdfEmailUseCase",
        )

        return runCatching {
            val response = httpClient.post(
                urlString = "${apiBaseUrlProvider.getBaseUrl()}/email/send-pdf",
            ) {
                timeout {
                    requestTimeoutMillis = 120_000L
                    socketTimeoutMillis = 120_000L
                    connectTimeoutMillis = 10_000L
                }
                contentType(ContentType.Application.Json)
                setBody(
                    SendPdfEmailRequest(
                        firstName = firstName,
                        lastName = lastName,
                        email = email,
                        pdfBase64 = pdfBase64,
                        subject = subject,
                        htmlContent = htmlContent,
                        fileName = fileName,
                    ),
                )
            }.body<SendPdfEmailResponse>()

            if (!response.success) {
                error(response.errorMessage ?: "Failed to send email")
            }
        }.fold(
            onSuccess = { Result.success(Unit) },
            onFailure = { e ->
                if (e.isNetworkConnectivityError()) {
                    Napier.w(message = "send_pdf_email: no internet", throwable = e, tag = "SendPdfEmailUseCase")
                    Result.failure(Exception("אין חיבור לאינטרנט. נסו שוב כשיהיה חיבור."))
                } else {
                    Napier.e(message = "send_pdf_email: unexpected error", throwable = e, tag = "SendPdfEmailUseCase")
                    Result.failure(e)
                }
            },
        )
    }
}
